import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Loader2, Save, Search, Shirt, X } from 'lucide-react';
import type { VirtualUser, WardrobeItem } from '../../api/virtualUser';
import { createCompose, DRESS_SLOTS, type ComposeItem } from '../../api/compose';
import { getBodyProfile, type BodyProfile } from '../../api/bodyProfile';
import { BodySilhouette } from '../body/BodySilhouette';
import { categoryRoot, presentRootCategories, rootCategoryLabel } from '../../lib/wardrobeCategory';
import { useWardrobeFeed } from '../../hooks/useWardrobeFeed';
import { startPointerSession } from '../../lib/pointerSession';
import { VirtualUserSelect } from '../shared/VirtualUserSelect';
import { WardrobeGrid } from '../compose/WardrobeGrid';

interface Props {
  virtualUsers: VirtualUser[];
  selected: VirtualUser | null;
  onSelect: (u: VirtualUser) => void;
}

const SLOT_LAYOUT: Record<string, { top: string; left: string; width: string; height: string }> = {
  tops: { top: '18%', left: '28%', width: '44%', height: '28%' },
  dresses: { top: '18%', left: '26%', width: '48%', height: '52%' },
  bottoms: { top: '44%', left: '30%', width: '40%', height: '32%' },
  footwear: { top: '78%', left: '32%', width: '36%', height: '16%' },
  accessories: { top: '8%', left: '58%', width: '28%', height: '14%' },
  bags: { top: '40%', left: '68%', width: '24%', height: '22%' },
  jewelry: { top: '12%', left: '18%', width: '20%', height: '12%' },
};

export function DressPage({ virtualUsers, selected, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [activeRoot, setActiveRoot] = useState('all');
  const [activeSlot, setActiveSlot] = useState<string>('tops');
  const [slots, setSlots] = useState<Record<string, ComposeItem | undefined>>({});
  const [body, setBody] = useState<BodyProfile | null>(null);
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<{ slot: string; ox: number; oy: number; cx: number; cy: number } | null>(null);

  const { items, loading, loadingMore, hasMore, counts, loadMore } = useWardrobeFeed(
    selected?.id,
    search,
    activeRoot === 'all' ? activeSlot : activeRoot,
    'category'
  );
  const roots = useMemo(() => presentRootCategories(Object.keys(counts)), [counts]);

  useEffect(() => {
    setSlots({});
    setMsg(null);
    setQuery('');
    setTitle('');
  }, [selected?.id]);

  useEffect(() => {
    if (!selected) {
      setBody(null);
      return;
    }
    let cancelled = false;
    getBodyProfile(selected.id)
      .then((p) => {
        if (!cancelled) setBody(p);
      })
      .catch(() => {
        if (!cancelled) setBody(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  const usedIds = useMemo(
    () => new Set(Object.values(slots).filter(Boolean).map((s) => s!.cloth_id)),
    [slots]
  );

  const lookItems = useMemo(
    () =>
      Object.entries(slots)
        .filter(([, v]) => v)
        .map(([, v]) => v!)
        .sort((a, b) => a.z_index - b.z_index),
    [slots]
  );

  const assignItem = (item: WardrobeItem) => {
    const slot = activeSlot || categoryRoot(item.category);
    setSlots((prev) => ({
      ...prev,
      [slot]: {
        slot,
        cloth_id: item.cloth_id,
        id_type: item.id_type,
        name: item.name,
        category: item.category,
        thumb: item.thumb,
        offset_x: 0,
        offset_y: 0,
        scale: 1,
        z_index: DRESS_SLOTS.findIndex((s) => s.id === slot) + 1,
      },
    }));
  };

  const clearSlot = (slot: string) => {
    setSlots((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
  };

  const patchSlot = (slot: string, patch: Partial<ComposeItem>) => {
    setSlots((prev) => {
      const cur = prev[slot];
      if (!cur) return prev;
      return { ...prev, [slot]: { ...cur, ...patch } };
    });
  };

  const onSlotPointerDown = (
    e: ReactPointerEvent,
    slot: string,
    item: ComposeItem
  ) => {
    e.stopPropagation();
    setActiveSlot(slot);
    setActiveRoot(slot);
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    dragOrigin.current = {
      slot,
      ox: item.offset_x,
      oy: item.offset_y,
      cx: e.clientX,
      cy: e.clientY,
    };
    startPointerSession(
      e,
      {
        onDragMove: (x, y) => {
          const origin = dragOrigin.current;
          if (!origin || origin.slot !== slot) return;
          const dx = (x - origin.cx) / rect.width;
          const dy = (y - origin.cy) / rect.height;
          patchSlot(slot, {
            offset_x: Math.max(-0.45, Math.min(0.45, origin.ox + dx)),
            offset_y: Math.max(-0.45, Math.min(0.45, origin.oy + dy)),
          });
        },
        onDragEnd: () => {
          dragOrigin.current = null;
        },
        onDragCancel: () => {
          dragOrigin.current = null;
        },
      },
      { immediate: true }
    );
  };

  const save = async () => {
    if (!selected) return;
    if (lookItems.length === 0) {
      setMsg('请先选择至少一件单品');
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await createCompose({
        virtual_user_id: selected.id,
        title: title.trim() || `Look · ${selected.display_name || selected.user_id}`,
        description: query.trim(),
        query: query.trim(),
        tags: lookItems.map((i) => i.slot).filter((v, i, a) => a.indexOf(v) === i),
        thumbnail_url: lookItems[0]?.thumb || body?.body_thumb || '',
        items: lookItems,
      });
      setMsg('已保存，并写回 recommend_outfit');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-12 border-b border-[var(--line)] bg-[var(--paper)] px-4 flex items-center gap-3 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Shirt className="w-4 h-4" />
          试穿搭配
        </div>
        <VirtualUserSelect virtualUsers={virtualUsers} selected={selected} onSelect={onSelect} />
        <input
          className="ml-2 flex-1 max-w-md text-sm border border-[var(--line)] rounded px-2 py-1.5"
          placeholder="用户需求 / 场景（将写入 query）"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="btn-primary text-sm" disabled={saving || !selected} onClick={save}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存造型
        </button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_1fr_280px]">
        {/* wardrobe */}
        <div className="min-h-0 flex flex-col border-r border-[var(--line)] bg-[var(--paper)]">
          <div className="p-3 border-b border-[var(--line)] space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
              <input
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-[var(--line)] rounded"
                placeholder="搜索衣橱"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {DRESS_SLOTS.map((s) => (
                <button
                  key={s.id}
                  className={`btn-ghost text-[11px] ${activeSlot === s.id ? 'bg-[var(--accent-soft)]' : ''}`}
                  onClick={() => {
                    setActiveSlot(s.id);
                    setActiveRoot(s.id);
                  }}
                >
                  {s.label}
                  {slots[s.id] ? ' ·' : ''}
                </button>
              ))}
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-thin">
            <WardrobeGrid
              items={items}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onLoadMore={loadMore}
              usedIds={usedIds}
              scrollRoot={scrollRef}
              pickMode
              onPickItem={assignItem}
            />
          </div>
        </div>

        {/* body stage */}
        <div className="min-h-0 relative flex flex-col items-center justify-center bg-[linear-gradient(180deg,#eeeef2,#f8f8fa)] gap-3 p-4">
          <div ref={stageRef} className="relative w-[min(420px,70%)] aspect-[3/5] touch-none">
            {body ? (
              <BodySilhouette
                profile={body}
                className="absolute inset-0 h-full w-full pointer-events-none"
              />
            ) : (
              <div className="absolute left-1/2 bottom-[8%] -translate-x-1/2 w-[42%] h-[78%] rounded-t-[45%] bg-[#d4d4d8] pointer-events-none" />
            )}
            {body?.body_thumb && (
              <img
                src={body.body_thumb}
                alt=""
                className="absolute right-0 bottom-0 h-[28%] w-auto object-contain opacity-80 pointer-events-none"
                draggable={false}
              />
            )}
            {lookItems.map((it) => {
              const layout = SLOT_LAYOUT[it.slot] ?? SLOT_LAYOUT.accessories;
              const active = activeSlot === it.slot;
              return (
                <div
                  key={it.cloth_id}
                  className={`absolute cursor-grab active:cursor-grabbing ${
                    active ? 'ring-2 ring-[var(--ink)] ring-offset-1 rounded' : ''
                  }`}
                  style={{
                    top: layout.top,
                    left: layout.left,
                    width: layout.width,
                    height: layout.height,
                    zIndex: it.z_index + (active ? 20 : 0),
                    transform: `translate(${(it.offset_x || 0) * 100}%, ${(it.offset_y || 0) * 100}%) scale(${it.scale || 1})`,
                    transformOrigin: 'center center',
                  }}
                  onPointerDown={(e) => onSlotPointerDown(e, it.slot, it)}
                  onWheel={(e) => {
                    e.preventDefault();
                    const next = Math.max(0.4, Math.min(2.2, (it.scale || 1) + (e.deltaY < 0 ? 0.05 : -0.05)));
                    patchSlot(it.slot, { scale: Number(next.toFixed(2)) });
                  }}
                >
                  <img
                    src={it.thumb}
                    alt={it.name}
                    className="w-full h-full object-contain drop-shadow-md pointer-events-none"
                    draggable={false}
                  />
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-[var(--muted)]">拖拽微调位置 · 滚轮缩放 · 右侧可精调</div>
        </div>

        {/* slots panel */}
        <aside className="min-h-0 overflow-y-auto border-l border-[var(--line)] bg-[var(--paper)] p-4 space-y-4 scrollbar-thin">
          <label className="block text-xs text-[var(--muted)]">
            造型标题
            <input
              className="mt-1 w-full text-sm border border-[var(--line)] rounded px-2 py-1.5"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="可选"
            />
          </label>

          <div className="space-y-2">
            <div className="text-xs font-medium">已选槽位</div>
            {DRESS_SLOTS.map((s) => {
              const it = slots[s.id];
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-2 p-2 rounded border ${
                    activeSlot === s.id ? 'border-[var(--ink)]' : 'border-[var(--line)]'
                  }`}
                  onClick={() => {
                    setActiveSlot(s.id);
                    setActiveRoot(s.id);
                  }}
                >
                  <div className="w-10 h-10 bg-[var(--paper-2)] rounded overflow-hidden shrink-0 flex items-center justify-center">
                    {it?.thumb ? (
                      <img src={it.thumb} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-[9px] text-[var(--muted)]">空</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium">{s.label}</div>
                    <div className="text-[10px] text-[var(--muted)] truncate">
                      {it?.name || '点击左侧衣橱选择'}
                    </div>
                  </div>
                  {it && (
                    <button
                      className="btn-ghost p-1 text-[var(--muted)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSlot(s.id);
                      }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {slots[activeSlot] && (
            <div className="space-y-2 border-t border-[var(--line)] pt-3">
              <div className="text-xs font-medium">微调 · {DRESS_SLOTS.find((s) => s.id === activeSlot)?.label}</div>
              <label className="block text-xs text-[var(--muted)]">
                <div className="flex justify-between mb-1">
                  <span>缩放</span>
                  <span>{(slots[activeSlot]!.scale || 1).toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min={0.4}
                  max={2.2}
                  step={0.01}
                  value={slots[activeSlot]!.scale || 1}
                  onChange={(e) => patchSlot(activeSlot, { scale: Number(e.target.value) })}
                  className="w-full"
                />
              </label>
              <button
                className="btn-ghost text-xs w-full"
                onClick={() => patchSlot(activeSlot, { offset_x: 0, offset_y: 0, scale: 1 })}
              >
                重置位置/缩放
              </button>
            </div>
          )}

          {roots.length > 0 && (
            <div className="text-[10px] text-[var(--muted)]">
              衣橱根类：{roots.map((r) => rootCategoryLabel(r)).join(' / ')}
            </div>
          )}
          {msg && <div className="text-xs text-center text-[var(--muted)]">{msg}</div>}
        </aside>
      </div>
    </div>
  );
}
